import { useCallback, useMemo } from 'react';

import { useToast } from '@chakra-ui/react';
import { downloadCSVWithColsAndRows } from 'Deer/intent/Reviews/hooks/useReviewsIntentElllipsisValues';
import { FILTERED_COLUMNS_IDS_ADMIN_REPORTS } from 'Deer/pages/Company/DisciplinaryActions/MyCompanyAdminReportsGrid';
import { getAdminReportsApi } from 'Deer/pages/MyData/DisciplinaryActions/dataHooks';
import { format } from 'date-fns';
import { useSelectedView } from 'reducers/viewsSlice';

import { Download } from 'assets/assetsV1/iconComponents';

import { AgGridKebabMenuOptions } from 'uiToolkitV1/AgGridWrapperV2/types';

import { useSelectDisciplinaryActionsReportDateRange } from '../slice/DisciplinaryActionsIntentSlice';

export const useDisciplinaryActionAdminReportIntentEllipsisValues = ({ gridRef }) => {
  const cohortDetails = useSelectedView();
  const reportDateRange = useSelectDisciplinaryActionsReportDateRange();

  const formattedReportDateRange = useMemo(
    () => ({
      startDate: format(reportDateRange.startDate, 'yyyy-MM-dd'),
      endDate: format(reportDateRange.endDate, 'yyyy-MM-dd'),
    }),
    [reportDateRange.startDate, reportDateRange.endDate]
  );

  const toast = useToast();

  const currentTableCsvExport = useCallback(() => {
    gridRef.current.api.exportDataAsCsv({
      fileName: 'disciplinary_action_current_view_admin_report.csv',
      columnKeys: FILTERED_COLUMNS_IDS_ADMIN_REPORTS,
      processCellCallback(params) {
        const { value } = params;

        return value || 'N.E.D';
      },
    });
  }, [gridRef]);

  const handleDetailedDownload = useCallback(async () => {
    if (!gridRef?.current?.api) return;

    try {
      const res = await getAdminReportsApi({
        cohortId: cohortDetails?.id,
        limit: null,
        pivotMode: false,
        rowGroupCols: [
          {
            id: 'EMPLOYEE',
            displayName: 'Employee',
            field: 'EMPLOYEE',
          },
        ],
        pivotCols: [],
        filterModel: {},
        baseFilter: formattedReportDateRange,
      });

      const allRowsData = res?.entity?.rows ?? [];
      const formattedAllRowsData = allRowsData.map((row) => ({
        ...row,
        EMPLOYEE: row?.EMPLOYEE?.displayName,
        VIOLATIONS_BY_SEVERITY: (row?.VIOLATIONS_BY_SEVERITY ?? [])
          ?.map((violation) => violation?.label?.toLowerCase())
          .join(', '),
        VIOLATIONS_BY_TYPE: Object.entries(row?.VIOLATIONS_BY_TYPE ?? {})
          .map(([key, value]) => `${key}: ${value}`)
          .join(', '),
      }));
      const columnDefs = res?.entity?.columns;
      const filteredColumns = columnDefs.filter((col) => FILTERED_COLUMNS_IDS_ADMIN_REPORTS.includes(col.columnId));

      const formattedColumnDefs = filteredColumns.map((field) => ({
        colId: field.columnId,
        displayName: field.displayName,
      }));

      // Generate CSV
      downloadCSVWithColsAndRows(
        formattedColumnDefs,
        formattedAllRowsData,
        'disciplinary_action_all_rows_admin_report.csv'
      );
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error downloading admin report',
        status: 'error',
      });
    }
  }, [cohortDetails?.id, formattedReportDateRange, gridRef, toast]);

  const ellipsisOptions = useMemo<AgGridKebabMenuOptions>(() => {
    const options: AgGridKebabMenuOptions = [
      {
        label: 'Download report',
        Icon: Download,
        type: 'subMenu',
        onClick: () => {},
        subOptions: [
          {
            label: 'Current View Download',
            onClick: () => currentTableCsvExport(),
            description: `Download admin report for current view`,
          },
          {
            label: 'Detailed Download',
            onClick: () => handleDetailedDownload(),
            description: `Download admin report for all employees`,
          },
        ],
      },
    ];

    return options;
  }, [currentTableCsvExport, handleDetailedDownload]);

  return ellipsisOptions;
};
